import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ComprobantesIncluidosTable from "./ComprobantesIncluidosTable";
import EditorRetencionesMedio from "./EditorRetencionesMedio";
import PanelChequeMedio from "./PanelChequeMedio";
import PropuestaPagoMedios from "./PropuestaPagoMedios";
import ResumenSeleccion from "./ResumenSeleccion";
import SeleccionCuentasOrigen, { nuevaFila } from "./SeleccionCuentasOrigen";
import { getSaldoCompensacionProveedor } from "./ApiServices";
import {
  AJUSTE_AUTONOMIA_VARIOS,
  CONCEPTOS_AJUSTE,
  ETIQUETAS_CONCEPTO_AJUSTE,
  configPagoProveedorVacia,
  formatCuit,
  formatMoneda,
  fechaAMoment,
  hayMedioActivo,
  mediosResumenProveedorPreview,
  obtenerResumenPreview,
  requiereMediosPago,
  restanteMediosProveedor,
  restanteMediosVsTotal,
  sumarImportesCuentas,
  sumaImportesMedios,
  totalParaMedios,
} from "./utils";

const MedioPanel = ({
  titulo,
  descripcion,
  checked,
  onToggle,
  disabled = false,
  children,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      mb: 1.25,
      border: "1px solid",
      borderColor: checked ? "primary.main" : "#e0e0e0",
      backgroundColor: checked ? "rgba(36, 113, 163, 0.04)" : "#fff",
      borderRadius: 1.5,
    }}
  >
    <FormControlLabel
      sx={{ m: 0, width: "100%", alignItems: "flex-start" }}
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={disabled}
          size="small"
        />
      }
      label={
        <Box sx={{ pt: 0.25 }}>
          <Typography variant="body2" fontWeight={600}>
            {titulo}
          </Typography>
          {descripcion ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {descripcion}
            </Typography>
          ) : null}
        </Box>
      }
    />
    <Collapse in={checked}>{children ? <Box sx={{ mt: 1.25, pl: 4 }}>{children}</Box> : null}</Collapse>
  </Paper>
);

/**
 * Importe con borrador local: Asignado/Restante solo se actualizan al salir del campo (blur).
 */
const CampoImporte = ({
  label = "Importe",
  value,
  onChange,
  max,
  helperText,
}) => {
  const [borrador, setBorrador] = useState(
    value != null && value !== "" ? String(value) : ""
  );
  const enfocadoRef = useRef(false);

  useEffect(() => {
    if (enfocadoRef.current) return;
    setBorrador(value != null && value !== "" ? String(value) : "");
  }, [value]);

  const confirmar = () => {
    enfocadoRef.current = false;
    const raw = String(borrador ?? "").trim().replace(",", ".");
    if (raw === "") {
      if (String(value ?? "") !== "") onChange("");
      setBorrador("");
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) {
      setBorrador(value != null && value !== "" ? String(value) : "");
      return;
    }
    const redondeado = Math.round(n * 100) / 100;
    const next = String(redondeado);
    setBorrador(next);
    if (String(value ?? "") !== next) onChange(next);
  };

  return (
    <TextField
      size="small"
      label={label}
      type="number"
      value={borrador}
      onFocus={() => {
        enfocadoRef.current = true;
      }}
      onChange={(e) => setBorrador(e.target.value)}
      onBlur={confirmar}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.target.blur();
        }
      }}
      onWheel={(e) => e.target.blur()}
      inputProps={{ min: 0, step: 0.01, ...(max != null ? { max } : {}) }}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
      }}
      helperText={helperText}
      sx={{ maxWidth: 220 }}
    />
  );
};

const TabMedioPago = ({
  comprobantesTabla,
  preview,
  previewLoading,
  previewError,
  previewMediosLote = null,
  previewMediosLoading = false,
  previewMediosError = "",
  medioPago,
  onMedioPagoChange,
  cuentasBancarias,
  loadingCuentas,
  cuitProveedor = "",
  compensacionHabilitada = true,
  proveedores = [],
  /** En edición unificada los comprobantes se editan arriba; no repetir la tabla. */
  mostrarComprobantes = true,
  /** Permite editar importes a pagar en la tabla de comprobantes (paso Medio / lote). */
  comprobantesEditables = false,
  importesAPagar = {},
  onImportesAPagarChange,
  onQuitarComprobante,
  puedeQuitarComprobante,
  cantidadSeleccionada = 0,
  filasSeleccionadas = [],
  configPagoPorProveedor = null,
  onConfigPagoChange,
  onEditarRetencion,
}) => {
  const {
    fechaPago,
    observaciones,
    efectivo,
    transferencia,
    compensacion,
    debitoAutomatico,
    tarjetaCredito,
    ajuste,
    cheque,
  } = medioPago;

  const modoPropuesta =
    configPagoPorProveedor != null && proveedores.length > 0;
  const multiProveedor = proveedores.length > 1;

  const resumenPreview = obtenerResumenPreview(preview);
  // Solo con preview real y selección: total 0 no debe confundirse con "sin selección".
  const cantidadSeleccion =
    Number(cantidadSeleccionada) > 0
      ? Number(cantidadSeleccionada)
      : (filasSeleccionadas?.length || comprobantesTabla?.length || 0);
  const cantidad = cantidadSeleccion;
  const totalAPagar =
    cantidad > 0 && resumenPreview?.total_a_pagar != null
      ? Number(resumenPreview.total_a_pagar)
      : null;
  const sinImporteNeto =
    !previewLoading &&
    totalAPagar != null &&
    !requiereMediosPago(totalAPagar);
  const totalParaDistribuir =
    cantidad > 0 ? totalParaMedios(totalAPagar ?? 0) : 0;
  // Sin comprobantes: asignado/restante en cero (como OP nueva).
  const asignado = cantidad > 0 ? sumaImportesMedios(medioPago) : 0;
  const restante =
    cantidad > 0
      ? restanteMediosVsTotal(medioPago, totalParaDistribuir)
      : 0;
  const distribucionOk =
    totalAPagar != null &&
    Math.abs(restante) < 0.02 &&
    (asignado > 0 || sinImporteNeto);
  const usaPreviewMediosLote = Boolean(previewMediosLote?.proveedores?.length);
  const chequesNoDisponibles = previewMediosLote?.cheques_no_disponibles ?? [];

  const distribucionOkLote = usaPreviewMediosLote
    ? Boolean(previewMediosLote.medios_lote_ok)
    : distribucionOk;

  const cuitCompensacion = useMemo(
    () => String(cuitProveedor || "").replace(/\D/g, ""),
    [cuitProveedor]
  );
  const compensacionReqId = useRef(0);

  const patchMedio = (clave, cambios) => {
    onMedioPagoChange({ [clave]: cambios });
  };

  const cargarCompensacion = async () => {
    if (!compensacionHabilitada || !cuitCompensacion) {
      patchMedio("compensacion", {
        activo: true,
        saldoDisponible: 0,
        idCliente: null,
        razonSocial: null,
        error: compensacionHabilitada
          ? "Sin CUIT de proveedor para consultar compensación"
          : "La compensación solo está disponible con un único proveedor",
        loading: false,
      });
      return;
    }

    const reqId = ++compensacionReqId.current;
    patchMedio("compensacion", {
      activo: true,
      loading: true,
      error: null,
    });

    try {
      const data = await getSaldoCompensacionProveedor(cuitCompensacion);
      if (reqId !== compensacionReqId.current) return;

      const saldo = Number(data.saldo_disponible) || 0;
      const sinSaldo = saldo <= 0;
      patchMedio("compensacion", {
        loading: false,
        saldoDisponible: saldo,
        idCliente: data.id_cliente ?? null,
        razonSocial: data.razon_social ?? null,
        error: sinSaldo
          ? data.mensaje || "No hay saldo a favor disponible para compensar"
          : null,
        importe:
          !sinSaldo && restante > 0
            ? String(Math.min(saldo, restante))
            : medioPago.compensacion?.importe || "",
      });
    } catch (err) {
      if (reqId !== compensacionReqId.current) return;
      patchMedio("compensacion", {
        loading: false,
        saldoDisponible: 0,
        idCliente: null,
        error: err.message || "No se pudo consultar el saldo de compensación",
      });
    }
  };

  // Si el neto queda en cero (o negativo) los medios cargados dejan de aplicar.
  useEffect(() => {
    if (!sinImporteNeto || !hayMedioActivo(medioPago)) return;
    onMedioPagoChange({
      efectivo: { activo: false, importe: "" },
      transferencia: { activo: false, cuentasOrigen: [] },
      compensacion: { activo: false, importe: "" },
      debitoAutomatico: { activo: false, importe: "" },
      tarjetaCredito: { activo: false, importe: "" },
      ajuste: { activo: false, importe: "" },
      cheque: { activo: false, recibidos: [], emitidos: [] },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sinImporteNeto]);

  // Las cuentas de transferencia se crean solo al activar el medio (toggle),
  // no se reintroducen solas si el usuario las quita o desactiva la transferencia.

  const toggleEfectivo = (activo) => {
    if (multiProveedor) {
      patchMedio("efectivo", {
        activo,
        importe: "",
        porProveedor: activo ? { ...(efectivo?.porProveedor || {}) } : {},
      });
      return;
    }
    const sugerido =
      activo && restante > 0 ? String(Math.round(restante * 100) / 100) : "";
    patchMedio("efectivo", {
      activo,
      importe: activo ? efectivo?.importe || sugerido : "",
    });
  };

  const actualizarEfectivoProveedor = (cuit, importe) => {
    const cuitDig = String(cuit || "").replace(/\D/g, "");
    patchMedio("efectivo", {
      porProveedor: {
        ...(efectivo?.porProveedor || {}),
        [cuitDig]: importe,
      },
    });
  };

  const toggleTransferencia = (activo) => {
    if (!activo) {
      // Reemplazo explícito: no dejar cuentas ni reactivar por efectos.
      patchMedio("transferencia", { activo: false, cuentasOrigen: [] });
      return;
    }
    const filas =
      transferencia?.cuentasOrigen?.length > 0
        ? transferencia.cuentasOrigen
        : [
            nuevaFila(
              restante > 0
                ? Math.round(restante * 100) / 100
                : totalParaDistribuir || ""
            ),
          ];
    patchMedio("transferencia", { activo: true, cuentasOrigen: filas });
  };

  const toggleCompensacion = (activo) => {
    if (!activo) {
      patchMedio("compensacion", {
        activo: false,
        importe: "",
        error: null,
        loading: false,
      });
      return;
    }
    cargarCompensacion();
  };

  const toggleDebito = (activo) => {
    patchMedio("debitoAutomatico", {
      activo,
      importe: activo
        ? debitoAutomatico?.importe ||
          (restante > 0 ? String(Math.round(restante * 100) / 100) : "")
        : "",
      ultimos4: activo ? debitoAutomatico?.ultimos4 || "" : "",
      referencia: activo ? debitoAutomatico?.referencia || "" : "",
    });
  };

  const toggleTarjeta = (activo) => {
    patchMedio("tarjetaCredito", {
      activo,
      importe: activo
        ? tarjetaCredito?.importe ||
          (restante > 0 ? String(Math.round(restante * 100) / 100) : "")
        : "",
      ultimos4: activo ? tarjetaCredito?.ultimos4 || "" : "",
      banco: activo ? tarjetaCredito?.banco || "" : "",
      referencia: activo ? tarjetaCredito?.referencia || "" : "",
    });
  };

  const toggleCheque = (activo) => {
    if (!activo) {
      patchMedio("cheque", { activo: false, recibidos: [], emitidos: [] });
      return;
    }
    patchMedio("cheque", { activo: true });
  };

  const toggleAjuste = (activo) => {
    const sugerido =
      activo && restante > 0
        ? String(Math.round(restante * 100) / 100)
        : "";
    patchMedio("ajuste", {
      activo,
      importe: activo ? ajuste?.importe || sugerido : "",
      concepto: activo
        ? ajuste?.concepto || CONCEPTOS_AJUSTE.varios
        : CONCEPTOS_AJUSTE.varios,
      descripcion: activo ? ajuste?.descripcion || "" : "",
    });
  };

  const conceptoAjuste = ajuste?.concepto || CONCEPTOS_AJUSTE.varios;
  const esAjusteVarios = conceptoAjuste === CONCEPTOS_AJUSTE.varios;
  const importeAjusteNum = Number(String(ajuste?.importe ?? "").replace(",", ".")) || 0;
  const ajusteVariosExcede =
    esAjusteVarios &&
    ajuste?.activo &&
    importeAjusteNum > AJUSTE_AUTONOMIA_VARIOS;

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {mostrarComprobantes ? (
        <Grid item xs={12}>
          <ComprobantesIncluidosTable
            filas={comprobantesTabla}
            cantidad={cantidad}
            editable={comprobantesEditables}
            importesAPagar={importesAPagar}
            onImportesAPagarChange={onImportesAPagarChange}
            onQuitarComprobante={onQuitarComprobante}
            puedeQuitarFila={puedeQuitarComprobante}
          />
        </Grid>
      ) : null}

      <Grid item xs={12} lg={8}>
        {onEditarRetencion ? (
          <Box sx={{ mb: 2 }}>
            <EditorRetencionesMedio
              preview={preview}
              onEditarRetencion={onEditarRetencion}
              disabled={previewLoading}
            />
          </Box>
        ) : null}

        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Medios de pago
          </Typography>

          {previewLoading && totalAPagar == null ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Calculando total a pagar y retenciones…
            </Alert>
          ) : null}
          {sinImporteNeto ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              El total neto de esta orden es {formatMoneda(totalAPagar)}: las
              retenciones y los créditos aplicados absorben el importe, así que
              no hay nada para transferir. La orden se registra como ajuste, sin
              medios de pago.
            </Alert>
          ) : (
          <>
          {previewMediosLoading ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Validando medios de pago con el servidor…
            </Alert>
          ) : null}
          {previewMediosError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewMediosError}
            </Alert>
          ) : null}
          {chequesNoDisponibles.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {chequesNoDisponibles.length === 1
                ? "Un cheque seleccionado ya no está disponible en cartera."
                : `${chequesNoDisponibles.length} cheques seleccionados ya no están disponibles en cartera.`}
              {chequesNoDisponibles
                .map((ch) => ch.nro_cheque || ch.id)
                .filter(Boolean)
                .slice(0, 5)
                .join(", ")}
            </Alert>
          ) : null}
          {modoPropuesta ? (
            <PropuestaPagoMedios
              proveedores={proveedores}
              configPagoPorProveedor={configPagoPorProveedor}
              onConfigPagoChange={onConfigPagoChange}
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
              editable
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Podés combinar varios medios. La suma de importes debe coincidir con
              el total a pagar.
            </Typography>
          )}

          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: distribucionOkLote ? "#e8f5e9" : "#fff8e1",
              border: "1px solid",
              borderColor: distribucionOkLote ? "#a5d6a7" : "#ffe082",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              Total a pagar:{" "}
              <strong>
                {totalAPagar != null ? formatMoneda(totalAPagar) : "—"}
              </strong>
            </Typography>
            <Typography variant="body2">
              Asignado: <strong>{formatMoneda(asignado)}</strong>
            </Typography>
            <Typography
              variant="body2"
              color={Math.abs(restante) < 0.02 ? "success.main" : "warning.main"}
            >
              {restante > 0.02
                ? "Falta asignar: "
                : restante < -0.02
                  ? "Sobra: "
                  : "Restante: "}
              <strong>
                {formatMoneda(Math.abs(restante) < 0.02 ? 0 : Math.abs(restante))}
              </strong>
            </Typography>
          </Box>

          {multiProveedor ? (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                bgcolor: "#fafbfc",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Diferencia por proveedor
                {usaPreviewMediosLote ? (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    (validado por servidor)
                  </Typography>
                ) : null}
              </Typography>
              {proveedores.map((prov) => {
                const cuitDig = String(prov.cuit || "").replace(/\D/g, "");
                const cfg =
                  configPagoPorProveedor?.[cuitDig] ??
                  configPagoPorProveedor?.[prov.cuit] ??
                  configPagoProveedorVacia();
                const resumenBack = mediosResumenProveedorPreview(
                  previewMediosLote,
                  cuitDig
                );
                const restanteProv = resumenBack
                  ? Number(resumenBack.faltante) || 0
                  : restanteMediosProveedor(
                      medioPago,
                      cuitDig,
                      prov.total,
                      cfg
                    );
                const excedenteProv = resumenBack
                  ? Number(resumenBack.excedente) || 0
                  : restanteProv < -0.02
                    ? Math.abs(restanteProv)
                    : 0;
                const totalMediosProv = resumenBack
                  ? Number(resumenBack.total_medios) || 0
                  : null;
                const totalPagarProv = resumenBack
                  ? Number(resumenBack.total_a_pagar ?? prov.total) || 0
                  : Number(prov.total) || 0;
                const diffProv = excedenteProv > 0.02 ? -excedenteProv : restanteProv;
                return (
                  <Box
                    key={cuitDig}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      py: 0.5,
                      borderBottom: "1px solid #eee",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: "50%" }}>
                      {prov.razon_social || formatCuit(cuitDig)}
                    </Typography>
                    <Typography variant="body2">
                      A pagar: <strong>{formatMoneda(totalPagarProv)}</strong>
                      {totalMediosProv != null ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          · Medios: {formatMoneda(totalMediosProv)}
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        diffProv > 0.02
                          ? "warning.main"
                          : diffProv < -0.02
                            ? "error.main"
                            : "success.main"
                      }
                    >
                      {diffProv > 0.02
                        ? "Faltante: "
                        : diffProv < -0.02
                          ? "Excedente: "
                          : "Cubierto: "}
                      <strong>
                        {formatMoneda(
                          Math.abs(diffProv) < 0.02 ? 0 : Math.abs(diffProv)
                        )}
                      </strong>
                    </Typography>
                    {resumenBack && resumenBack.medios_ok === false ? (
                      <Typography variant="caption" color="error.main" display="block">
                        {(resumenBack.errores_medios || []).join(" · ") ||
                          "Medios incompletos o inconsistentes"}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          ) : null}

          {modoPropuesta ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              La propuesta define transferencia/cheque por proveedor. Abajo
              podés activar el resto de medios (efectivo, compensación, tarjeta,
              etc.) o completar el detalle de cheques. La suma debe coincidir
              con el total a pagar.
            </Typography>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1.5, mt: -1 }}
            >
              Asignado y restante se actualizan al salir del campo de importe (Tab
              o clic fuera).
            </Typography>
          )}

          <MedioPanel
            titulo="Efectivo"
            descripcion={
              multiProveedor
                ? "Asigná el importe en efectivo a cada proveedor por separado"
                : "Pago en efectivo"
            }
            checked={Boolean(efectivo?.activo)}
            onToggle={toggleEfectivo}
          >
            {multiProveedor ? (
              <Stack spacing={1.5}>
                {proveedores.map((prov) => {
                  const cuitDig = String(prov.cuit || "").replace(/\D/g, "");
                  const cfg =
                    configPagoPorProveedor?.[cuitDig] ??
                    configPagoPorProveedor?.[prov.cuit] ??
                    configPagoProveedorVacia();
                  const restanteProv = restanteMediosProveedor(
                    medioPago,
                    cuitDig,
                    prov.total,
                    cfg
                  );
                  return (
                    <Box key={cuitDig}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        {prov.razon_social || formatCuit(cuitDig)}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          Faltante: {formatMoneda(Math.max(restanteProv, 0))}
                        </Typography>
                      </Typography>
                      <CampoImporte
                        label="Efectivo"
                        value={efectivo?.porProveedor?.[cuitDig] ?? ""}
                        onChange={(v) => actualizarEfectivoProveedor(cuitDig, v)}
                      />
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <CampoImporte
                value={efectivo?.importe ?? ""}
                onChange={(v) => patchMedio("efectivo", { importe: v })}
              />
            )}
          </MedioPanel>

          <MedioPanel
            titulo="Transferencia"
            descripcion={
              modoPropuesta
                ? "Cuentas origen según la propuesta por proveedor (podés ajustar importes)"
                : "Transferencia bancaria desde una o más cuentas origen"
            }
            checked={Boolean(transferencia?.activo)}
            onToggle={toggleTransferencia}
          >
            <SeleccionCuentasOrigen
              cuentasOrigen={transferencia?.cuentasOrigen ?? []}
              onChange={(filas) =>
                patchMedio("transferencia", { cuentasOrigen: filas })
              }
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
              totalAPagar={(() => {
                const sumaTransf = sumarImportesCuentas(
                  transferencia?.cuentasOrigen ?? []
                );
                return sumaTransf > 0
                  ? sumaTransf
                  : Math.max(restante, 0);
              })()}
            />
          </MedioPanel>

          <MedioPanel
            titulo="Cheque"
            descripcion="Endosar cheques disponibles de cartera o emitir uno nuevo en esta orden"
            checked={Boolean(cheque?.activo)}
            onToggle={toggleCheque}
          >
            <PanelChequeMedio
              cheque={cheque}
              onChange={(cambios) => patchMedio("cheque", cambios)}
              restante={restante}
              totalAPagar={totalAPagar}
              proveedores={proveedores}
              configPagoPorProveedor={configPagoPorProveedor || {}}
              medioPago={medioPago}
              chequesAsignadosLote={previewMediosLote?.cheques_asignados ?? null}
              previewMediosLote={previewMediosLote}
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
            />
          </MedioPanel>

          <MedioPanel
            titulo="Compensación"
            descripcion="Saldo a favor en cuenta corriente del cliente con el mismo CUIT"
            checked={Boolean(compensacion?.activo)}
            onToggle={toggleCompensacion}
            disabled={!compensacionHabilitada && !compensacion?.activo}
          >
            {!compensacionHabilitada ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                La compensación solo está disponible cuando hay un único
                proveedor seleccionado.
              </Alert>
            ) : null}
            {compensacion?.loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Consultando saldo a favor…
                </Typography>
              </Box>
            ) : null}
            {compensacion?.error ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {compensacion.error}
              </Alert>
            ) : null}
            {!compensacion?.loading &&
            !compensacion?.error &&
            Number(compensacion?.saldoDisponible) > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Saldo disponible:{" "}
                  <strong>{formatMoneda(compensacion.saldoDisponible)}</strong>
                  {compensacion.razonSocial
                    ? ` · ${compensacion.razonSocial}`
                    : ""}
                </Typography>
                <CampoImporte
                  value={compensacion?.importe ?? ""}
                  onChange={(v) => patchMedio("compensacion", { importe: v })}
                  max={Number(compensacion.saldoDisponible)}
                  helperText={`Máximo ${formatMoneda(compensacion.saldoDisponible)}`}
                />
              </>
            ) : null}
          </MedioPanel>

          <MedioPanel
            titulo="Débito automático"
            descripcion="Débito automático con tarjeta de crédito"
            checked={Boolean(debitoAutomatico?.activo)}
            onToggle={toggleDebito}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm="auto">
                <CampoImporte
                  value={debitoAutomatico?.importe ?? ""}
                  onChange={(v) =>
                    patchMedio("debitoAutomatico", { importe: v })
                  }
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField
                  size="small"
                  label="Últimos 4 dígitos"
                  value={debitoAutomatico?.ultimos4 ?? ""}
                  onChange={(e) =>
                    patchMedio("debitoAutomatico", {
                      ultimos4: e.target.value.replace(/\D/g, "").slice(0, 4),
                    })
                  }
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                  sx={{ width: 150 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Referencia (opcional)"
                  value={debitoAutomatico?.referencia ?? ""}
                  onChange={(e) =>
                    patchMedio("debitoAutomatico", {
                      referencia: e.target.value.slice(0, 80),
                    })
                  }
                />
              </Grid>
            </Grid>
          </MedioPanel>

          <MedioPanel
            titulo="Pago con tarjeta de crédito"
            descripcion="Cobro / pago con tarjeta de crédito"
            checked={Boolean(tarjetaCredito?.activo)}
            onToggle={toggleTarjeta}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm="auto">
                <CampoImporte
                  value={tarjetaCredito?.importe ?? ""}
                  onChange={(v) =>
                    patchMedio("tarjetaCredito", { importe: v })
                  }
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField
                  size="small"
                  label="Últimos 4 dígitos"
                  value={tarjetaCredito?.ultimos4 ?? ""}
                  onChange={(e) =>
                    patchMedio("tarjetaCredito", {
                      ultimos4: e.target.value.replace(/\D/g, "").slice(0, 4),
                    })
                  }
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                  sx={{ width: 150 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Banco / marca (opc.)"
                  value={tarjetaCredito?.banco ?? ""}
                  onChange={(e) =>
                    patchMedio("tarjetaCredito", {
                      banco: e.target.value.slice(0, 60),
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Referencia (opc.)"
                  value={tarjetaCredito?.referencia ?? ""}
                  onChange={(e) =>
                    patchMedio("tarjetaCredito", {
                      referencia: e.target.value.slice(0, 80),
                    })
                  }
                />
              </Grid>
            </Grid>
          </MedioPanel>

          <MedioPanel
            titulo="Ajuste"
            descripcion="Ítem aparte para salvar diferencias o reflejar deuda interna (p. ej. cheque rechazado)"
            checked={Boolean(ajuste?.activo)}
            onToggle={toggleAjuste}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="ajuste-concepto-label">Concepto</InputLabel>
                  <Select
                    labelId="ajuste-concepto-label"
                    label="Concepto"
                    value={conceptoAjuste}
                    onChange={(e) =>
                      patchMedio("ajuste", { concepto: e.target.value })
                    }
                  >
                    <MenuItem value={CONCEPTOS_AJUSTE.varios}>
                      {ETIQUETAS_CONCEPTO_AJUSTE.varios}
                    </MenuItem>
                    <MenuItem value={CONCEPTOS_AJUSTE.internos}>
                      {ETIQUETAS_CONCEPTO_AJUSTE.internos}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm="auto">
                <CampoImporte
                  value={ajuste?.importe ?? ""}
                  onChange={(v) => patchMedio("ajuste", { importe: v })}
                  max={esAjusteVarios ? AJUSTE_AUTONOMIA_VARIOS : undefined}
                  helperText={
                    esAjusteVarios
                      ? `Autonomía hasta ${formatMoneda(AJUSTE_AUTONOMIA_VARIOS)}`
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Descripción"
                  placeholder="Ej.: diferencia de redondeo, cheque rechazado, adelanto por flete…"
                  value={ajuste?.descripcion ?? ""}
                  onChange={(e) =>
                    patchMedio("ajuste", {
                      descripcion: e.target.value.slice(0, 250),
                    })
                  }
                  helperText="Obligatoria. Máx. 250 caracteres."
                />
              </Grid>
              {esAjusteVarios ? (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Ajustes Varios: para diferencias chicas como ítem aparte.
                    Autonomía hasta {formatMoneda(AJUSTE_AUTONOMIA_VARIOS)}.
                  </Alert>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Ajustes internos: p. ej. cheques rechazados cuando el
                    proveedor no emite documento, para reflejar la deuda en
                    cuenta.
                  </Alert>
                </Grid>
              )}
              {ajusteVariosExcede ? (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    El importe supera la autonomía de Ajustes Varios (
                    {formatMoneda(AJUSTE_AUTONOMIA_VARIOS)}). Usá Ajustes
                    internos u otro medio.
                  </Alert>
                </Grid>
              ) : null}
            </Grid>
          </MedioPanel>
          </>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
                <DatePicker
                  label="Fecha de pago"
                  value={fechaAMoment(fechaPago)}
                  onChange={(v) => onMedioPagoChange({ fechaPago: v })}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Observaciones (opcional)"
                multiline
                minRows={3}
                value={observaciones}
                onChange={(e) =>
                  onMedioPagoChange({
                    observaciones: e.target.value.slice(0, 250),
                  })
                }
                helperText={`${(observaciones || "").length} / 250`}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} lg={4}>
        <ResumenSeleccion
          preview={preview}
          previewLoading={previewLoading}
          previewError={previewError}
          formatMoneda={formatMoneda}
          cantidadSeleccionada={cantidad}
          filasSeleccionadas={
            filasSeleccionadas?.length
              ? filasSeleccionadas
              : comprobantesTabla
          }
          importesAPagar={importesAPagar}
        />
      </Grid>
    </Grid>
  );
};

export default TabMedioPago;
